import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getScenarios, saveSessionAsTestCase } from '../api/client'
import type { ReconciliationSession, Scenario } from '../types'

interface Props {
  session: ReconciliationSession
  onClose: () => void
  onSaved: (scenarioName: string, caseName: string) => void
}

function defaultName(session: ReconciliationSession): string {
  const parts: string[] = []
  if (session.supplier_name) parts.push(session.supplier_name)
  if (session.project_number) parts.push(session.project_number)
  if (!parts.length && session.ordrsp_filename) parts.push(session.ordrsp_filename.replace(/\.xml$/i, ''))
  return parts.join(' – ') || `Sessie ${session.id}`
}

export function SaveAsTestCaseModal({ session, onClose, onSaved }: Props) {
  const { t } = useTranslation()
  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [scenarioId, setScenarioId] = useState<number | null>(null)
  const [name, setName] = useState(defaultName(session))
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const nameRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    getScenarios()
      .then((list) => {
        setScenarios(list)
        if (list.length > 0) setScenarioId(list[0].id)
      })
      .catch(() => setError(t('save_testcase_modal.error_load_scenarios')))
  }, [t])

  useEffect(() => {
    nameRef.current?.focus()
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!scenarioId) { setError(t('save_testcase_modal.error_choose_scenario')); return }
    if (!name.trim()) { setError(t('save_testcase_modal.error_name_required')); return }
    setSaving(true)
    setError(null)
    try {
      await saveSessionAsTestCase(scenarioId, session.id, name.trim(), description.trim() || undefined)
      const scenario = scenarios.find((s) => s.id === scenarioId)
      onSaved(scenario?.name ?? '', name.trim())
    } catch (e) {
      setError(e instanceof Error ? e.message : t('save_testcase_modal.error_unknown'))
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('save_testcase_modal.title')}</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('save_testcase_modal.label_scenario')}</label>
            {scenarios.length === 0 && !error ? (
              <p className="text-sm text-gray-400 dark:text-gray-500">{t('common.loading')}</p>
            ) : (
              <select
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={scenarioId ?? ''}
                onChange={(e) => setScenarioId(Number(e.target.value))}
                disabled={saving}
              >
                {scenarios.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('save_testcase_modal.label_name')}</label>
            <input
              ref={nameRef}
              type="text"
              maxLength={200}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={saving}
              placeholder={t('save_testcase_modal.placeholder_name')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('save_testcase_modal.label_description')} <span className="text-gray-400 dark:text-gray-500 font-normal">({t('save_testcase_modal.optional')})</span>
            </label>
            <textarea
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={saving}
              placeholder={t('save_testcase_modal.placeholder_description')}
            />
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 rounded-lg px-3 py-2 space-y-0.5">
            {session.ordrsp_filename && <div>✓ OrderResponse: {session.ordrsp_filename}</div>}
            {session.desadv_filename && <div>✓ DespatchAdvice: {session.desadv_filename}</div>}
            {session.invoice_filename && <div>✓ Invoice: {session.invoice_filename}</div>}
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving || !scenarioId || !name.trim()}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-lg font-medium transition-colors"
            >
              {saving ? t('save_testcase_modal.btn_saving') : t('save_testcase_modal.btn_save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
